import { createApp } from 'vue';
import { createPinia } from 'pinia';
import App from './App.vue';
import router from './router';
import './assets/tailwind.css';
import { createToast } from './plugins/toastPlugin';
import notifyPlugin from './plugins/notifyPlugin';
import { useLanguageStore } from './store/languageStore.js';
import { useHeaderStore } from './store/headerStore.js';
import { useRouteStore } from './store/routeStore.js';
import { useUserStore } from './store/userStore.js';
import { useDeviceStore } from './store/deviceStore';
import { useDebugStore } from './store/debugStore.js';
import { setGlobalToast } from './store/debugStore.js';
import { usePageConfigStore } from './store/pageConfigStore.js';
import Toast from './components/Utils/Toast.vue';
import Translate from './components/Utils/Translate.vue';
import VueTelInput from 'vue-tel-input';
import 'vue-tel-input/vue-tel-input.css';
import "leaflet/dist/leaflet.css";

import ProxyObjectFuckery from "@/utils/ProxyObjectFuckery"
import TranslateFunc from "@/utils/TranslateFunc"


import * as Icons from "@heroicons/vue/24/solid";

const app = createApp(App);
const pinia = createPinia();

app.config.globalProperties.$apiUrl = process.env.VUE_APP_API_URL;

app.use(pinia);
app.use(VueTelInput);

// Create and use toast, then set global reference
const toastPlugin = createToast();
app.use(toastPlugin);

// Add notify plugin
app.use(notifyPlugin);

// Set global toast reference for stores
setGlobalToast({
  add: app.config.globalProperties.$toast.add,
  removeAllToasts: app.config.globalProperties.$toast.removeAllToasts,
  removeToast: app.config.globalProperties.$toast.removeToast
});

app.component('Toast', Toast);
app.component('Translate', Translate);

// Register Heroicons globally
Object.entries(Icons).forEach(([name, component]) => {
  app.component(name, component);
});

const languageStore = useLanguageStore();
const headerStore = useHeaderStore();
const routeStore = useRouteStore();
const userStore = useUserStore();
const deviceStore = useDeviceStore();
const debugStore = useDebugStore();
const pageConfigStore = usePageConfigStore();

// Attach stores to global properties
app.config.globalProperties.$languageStore = languageStore;
app.config.globalProperties.$headerStore = headerStore;
app.config.globalProperties.$routeStore = routeStore;
app.config.globalProperties.$userStore = userStore;
app.config.globalProperties.$deviceStore = deviceStore;
app.config.globalProperties.$debugStore = debugStore;
app.config.globalProperties.$pageConfigStore = pageConfigStore;
app.config.globalProperties.$Translate = TranslateFunc;
app.config.globalProperties.$ProxyObjectFuckery = ProxyObjectFuckery;


// --- moved startup logic into an async init() to avoid top-level await and to ensure proper routing ---

async function init() {
  try {
    // Load available languages first
    await languageStore.LoadLanguages();

    // Priority order: URL param > User preference > Default
    const urlLang = new URLSearchParams(window.location.search).get('lang');
    
    let languageToSet = null;
    
    if (urlLang) {
      languageToSet = languageStore.languages.find(l => l.code === urlLang || l.alternative === urlLang);
    }
    
    // Load user BEFORE setting language if no explicit lang choice
    // IMPORTANT: Await user loading to ensure token validation completes
    if (!languageToSet) {
      await userStore.loadUser(); // ← Changed to await
      if (userStore.user?.lang) {
        languageToSet = languageStore.languages.find(l => l.code === userStore.user.lang);
      }
    } else {
      // Even if we have explicit language, load user first
      await userStore.loadUser(); // ← Added await here too
    }
    
    // Set language once with all context
    if (languageToSet) {
      await languageStore.setLanguage(languageToSet);
    } else {
      await languageStore.loadLanguage(null);
    }

    // device detection and resize listener
    deviceStore.detectDevice();
    window.addEventListener('resize', () => {
      deviceStore.detectDevice();
    });

    // Fetch headers (adds dynamic routes) AFTER user is validated
    // This ensures the correct pages are loaded based on user permissions
    await headerStore.fetchHeaders(
      languageStore.selectedLanguage?.alternative ||
      languageStore.selectedLanguage?.code ||
      'cs'
    );

    // Now register and use the router
    app.use(router);

    const currentFull = window.location.pathname + window.location.search + window.location.hash;
    await router.replace(currentFull).catch(() => {});

    await router.isReady();
    app.mount('#app');
  } catch (error) {
    app.config.globalProperties.$startupError = error;
    window.__APP_STARTUP_ERROR__ = error;
    app.use(router);
    app.mount('#app');
  }
}

init();